import { DrawCustomModeThis } from "@mapbox/mapbox-gl-draw";
import booleanEqual from "@turf/boolean-equal";
import { Feature, Geometry, LineString, Point } from "geojson";
import { RBush } from "geojson-rbush";

import { feature as toFeature } from "fond/turf";
import { LayerIds } from "fond/types";
import { isLineString, isPoint } from "fond/types/geojson";

export const isPointConnected = (point: Feature<Point>, tree: RBush<Geometry, any>): boolean => {
  const collocatedFeatures = tree.search(point);

  return collocatedFeatures.features.some((f) => {
    if (f.properties?.layerId !== LayerIds.inStreet || f.geometry.type !== "LineString") return false;
    const vertices: Feature<Point>[] = f.geometry.coordinates.map((coord) => toFeature({ type: "Point", coordinates: coord }));
    return vertices.some((vertex) => booleanEqual(point.geometry, vertex.geometry));
  });
};

export const isPathConnected = (path: Feature<LineString>, tree: RBush<Geometry, any>): boolean => {
  // Both ends of a path must have a colocated ug_point
  const endPoints = [
    toFeature({ type: "Point", coordinates: path.geometry.coordinates.at(0) }),
    toFeature({ type: "Point", coordinates: path.geometry.coordinates.at(-1) }),
  ];

  return endPoints.every((point) => {
    const collocatedFeatures = tree.search(point).features.filter((f) => f.id !== path.id);
    return collocatedFeatures.some((f) => {
      if (f.properties?.layerId !== LayerIds.inStreetPoints || f.geometry.type !== "Point") return false;
      return booleanEqual(point.geometry, f.geometry);
    });
  });
};

export const isValid = (feature: Feature<Point | LineString>, tree: RBush<Geometry, any>): boolean => {
  if (isPoint(feature)) return isPointConnected(feature, tree);
  if (isLineString(feature)) return isPathConnected(feature, tree);
  return false;
};

/**
 * This function checks for collocated features (ug_path start and end vertices) at the given feature's location.
 * @param this The current context of the DrawCustomModeThis
 * @param point The feature to be checked for collocated features
 * @returns An array of collocated paths found at the feature's location
 */
export function pullInPaths(this: DrawCustomModeThis, point: Feature<Point>): Feature<LineString>[] {
  return this._ctx.api.getAll().features.filter((f) => {
    if (f.properties?.layerId !== LayerIds.inStreet || f.geometry.type !== "LineString") return false;
    const vertices: Feature<Point>[] = f.geometry.coordinates.map((coord) => toFeature({ type: "Point", coordinates: coord }));
    return vertices.some((vertex) => booleanEqual(point.geometry, vertex.geometry));
  }) as Feature<LineString>[];
}

/**
 * This function checks for collocated features (ug_points) at the start and end vertices of the ug_path provided.
 * @param this The current context of the DrawCustomModeThis
 * @param point The feature to be checked for collocated features
 * @returns An array of collocated points found at the feature's start and end vetices.
 */
export function pullInPoints(this: DrawCustomModeThis, path: Feature<LineString>): Feature<Point>[] {
  if (path.geometry.coordinates.length === 0) return [];

  return this._ctx.api.getAll().features.filter((f) => {
    if (f.properties?.layerId !== LayerIds.inStreetPoints || f.geometry.type !== "Point") return false;

    const vertices: Feature<Point>[] = [path.geometry.coordinates.at(0), path.geometry.coordinates.at(-1)].map((coord) =>
      toFeature({ type: "Point", coordinates: coord })
    );
    return vertices.some((vertex) => booleanEqual(f.geometry, vertex.geometry));
  }) as Feature<Point>[];
}
